<template>
  <BasePageCard>
    <div class="freight-table-add-carrier">
      <div class="freight-table-add-carrier__header">
        <h2 class="freight-table-add-carrier__title">Adicionar transportadora</h2>
        <p v-if="subtitle" class="freight-table-add-carrier__subtitle">
          {{ subtitle }}
        </p>
      </div>

      <div v-if="loading" class="freight-table-add-carrier__loading">
        <a-spin />
      </div>

      <div v-else class="freight-table-add-carrier__sections">
        <FreightTableFormSection
          :index="1"
          title="Identificação"
          subtitle="Configure as informações iniciais da tabela de frete"
          :open="false"
          done
          locked
          @locked-click="onLockedClick"
        />

        <FreightTableFormSection
          :index="2"
          title="Parceiro"
          subtitle="Informe a(s) transportadora(s) e o(s) cliente(s), caso seja um tipo de tabela receita, que irá ser composta."
          open
        >
          <FreightTableSupplierSection
            :value.sync="pendingSuppliers"
            :exclude-ids="linkedIds"
            :linked-table-label="subtitle"
          />
        </FreightTableFormSection>

        <FreightTableFormSection
          :index="3"
          title="Configurações e Rotas"
          subtitle="Cadastre o tipo de cálculo, vigência e valores negociados por rota"
          :open="false"
          done
          locked
          @locked-click="onLockedClick"
        />
      </div>

      <div class="freight-table-add-carrier__actions-bar">
        <a-button
          class="freight-table-add-carrier__cancel"
          :disabled="saving"
          @click="onCancel"
          >Cancelar</a-button
        >

        <a-button
          type="primary"
          :loading="saving"
          :disabled="!canSave"
          @click="onSave"
          >Salvar</a-button
        >
      </div>
    </div>
  </BasePageCard>
</template>

<script>
import { mapActions } from "vuex";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import FreightTableFormSection from "@/modules/FreightTables/components/Create/FreightTableFormSection.vue";
import FreightTableSupplierSection from "@/modules/FreightTables/components/Create/FreightTableSupplierSection.vue";
import { handleApiError } from "@/shared/utils/handleApiError";
import {
  FREIGHT_TABLE_LIST_PATH,
  draftPath,
  isFreightTablePath,
} from "@/modules/FreightTables/constants/freightTableRoutes";
import { mergeCarriersForSave } from "@/modules/FreightTables/utils/mergeCarriersForSave";

export default {
  name: "FreightTableAddCarrierView",
  components: {
    BasePageCard,
    FreightTableFormSection,
    FreightTableSupplierSection,
  },
  data() {
    return {
      loading: true,
      saving: false,
      linkedSuppliers: [],
      pendingSuppliers: [],
      linkedIds: [],
      codigo: null,
      descricao: "",
    };
  },
  computed: {
    freightTableId() {
      return Number(this.$route.params.id);
    },
    subtitle() {
      if (this.loading || !this.codigo) return "";
      return this.descricao
        ? `Tabela ${this.codigo} — ${this.descricao}`
        : `Tabela ${this.codigo}`;
    },
    canSave() {
      return !this.saving && this.pendingSuppliers.length > 0;
    },
  },
  created() {
    this.loadCarriers();
  },
  beforeRouteLeave(to, from, next) {
    if (!isFreightTablePath(to.path)) {
      this.resetListState();
    }
    next();
  },
  methods: {
    ...mapActions("freightTables", [
      "fetchFreightTableCarrierSuppliers",
      "updateFreightTableCarriers",
      "resetListState",
    ]),
    async loadCarriers() {
      this.loading = true;
      try {
        const data = await this.fetchFreightTableCarrierSuppliers({
          freightTableId: this.freightTableId,
        });

        if (data.isDraft) {
          this.goToDraft();
          return;
        }

        this.codigo = data.codigo;
        this.descricao = data.descricao;
        this.linkedSuppliers = data.suppliers || [];
        this.pendingSuppliers = [];
        this.linkedIds = data.linkedIds || [];
        this.loading = false;
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível carregar as transportadoras da tabela",
        );
        this.$router.replace(FREIGHT_TABLE_LIST_PATH);
      }
    },
    goToDraft() {
      this.$notification.info({
        message: "Tabela em rascunho",
        description:
          "Transportadoras de um rascunho são vinculadas no próprio fluxo de criação.",
      });
      this.$router.replace(draftPath(this.freightTableId));
    },
    onLockedClick() {
      this.$warning({
        title: "Etapa indisponível",
        content:
          "Nesta ação apenas a etapa Parceiro pode ser alterada. Para editar as demais, use o cadastro da tabela.",
        okText: "Entendi",
      });
    },
    onCancel() {
      this.$router.push(FREIGHT_TABLE_LIST_PATH);
    },
    async onSave() {
      if (!this.canSave) return;

      this.saving = true;
      try {
        await this.updateFreightTableCarriers({
          freightTableId: this.freightTableId,
          suppliers: mergeCarriersForSave(
            this.linkedSuppliers,
            this.pendingSuppliers,
            this.linkedIds,
          ),
        });
        this.$notification.success({
          message: "Transportadoras adicionadas",
          description:
            "As transportadoras incluídas já constam na tabela com status ativo.",
        });
        this.$router.push(FREIGHT_TABLE_LIST_PATH);
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível atualizar as transportadoras",
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

@freight-footer-reserve: 100px;

.freight-table-add-carrier {
  font-family: @font-primary;
  padding: 24px 32px @freight-footer-reserve;

  &__title {
    font-family: @font-primary;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin: 0;
  }

  &__subtitle {
    color: @color-text-light;
    margin: 4px 0 0;
  }

  &__header {
    margin-bottom: 24px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 64px 0;
  }

  &__actions-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 56px;
    background: #ffffff;
    box-shadow: 0px -2px 16px rgba(0, 0, 0, 0.16);
  }

  &__cancel {
    color: @color-danger-action;
    border-color: @color-danger-action;

    &:hover,
    &:focus {
      color: darken(@color-danger-action, 8%);
      border-color: darken(@color-danger-action, 8%);
    }
  }
}
</style>
